import { i18n } from 'boot/i18n';
import { documentId } from 'firebase/firestore';
import { isEmpty } from 'lodash';
import ProgramFoldersDB from 'src/fb/programFolders-db';
import ProgramsDB from 'src/fb/programs-db';
import { auth as useAuthStore } from 'src/store/auth';
import { programs as useProgramsStore } from 'src/store/programs';
import { shouldHideTrainingContent } from 'src/utils/ProgramUtils';
import { computed, ref } from 'vue';

import { notifyNegative, notifySuccess } from './interactions';

// eslint-disable-next-line import/prefer-default-export
export function useProgramFolders() {
  const authStore = useAuthStore();
  const programsStore = useProgramsStore();

  const userDetails = computed(() => authStore.userDetails);
  const currentClient = computed(() => authStore.currentClient);
  const currentFolderPath = computed(() => programsStore.currentFolderPath);
  const currentFolderGetter = computed(() => programsStore.currentFolder);

  const isLoadingProgramFolder = ref(false);
  const folders = ref([]);
  const fullFolderPath = ref([]);

  async function loadFolders() {
    const programFoldersDb = new ProgramFoldersDB();

    const clientFolders = await programFoldersDb.readAll([
      ['clinic', '==', userDetails.value.clinic],
      ['client', '==', currentClient.value.id],
      ['scope', '==', 'Programs'],
    ]);
    folders.value = clientFolders
      .filter((folder) => !shouldHideTrainingContent(folder, currentClient.value, authStore.userIsAdmin));
  } // loadFolders()

  function findFolder(folderId) {
    return folders.value.find(({ id }) => id === folderId);
  } // findFolder()

  async function loadProgramScopedFolders() {
    try {
      isLoadingProgramFolder.value = true;
      await loadFolders();
    } finally {
      isLoadingProgramFolder.value = false;
    }
  } // loadProgramsFromLibrary()

  function findTopLevelFolderRecursive(folder) {
    if (!folder.parentFolder || folder.id === folder.parentFolder) {
      return folder;
    }

    const folderSearchResult = folders.value.find(({ id }) => id === folder.parentFolder);
    return findTopLevelFolderRecursive(folderSearchResult);
  } // findTopLevelFolderRecursive()

  async function goToFolder(level) {
    const newCurrentFolderPath = currentFolderPath.value.slice(0, level + 1);
    await programsStore.setCurrentFolderPath(newCurrentFolderPath);
  } // goToFolder()

  function getFolderPath(programFolderId) {
    const currentFolder = folders.value.find((folder) => folder.id === programFolderId);
    const currentFolderName = currentFolder?.folderName;
    const parentFolderId = currentFolder?.parentFolder;

    if (currentFolderName) {
      fullFolderPath.value.unshift(currentFolderName);
    }

    if (parentFolderId) {
      getFolderPath(parentFolderId);
    }
  } // getFolderPath()

  async function changeFolder(folderId) {
    const folder = findFolder(folderId);

    if (folder) {
      await programsStore.setCurrentFolderPath([...currentFolderPath.value, folder]);
    }
  } // changeFolder()

  function getFolderIcon(programFolder) {
    if (programFolder.clinic === null) {
      return 'snippet_folder';
    }
    return programFolder.isShared ? 'folder_shared' : 'folder';
  } // getFolderIcon()

  async function createProgramFolder(folderName) {
    try {
      isLoadingProgramFolder.value = true;
      const parentFolderId = currentFolderGetter.value?.id ?? null;
      const programFoldersDb = new ProgramFoldersDB();
      const programFolder = {
        clinic: userDetails.value.clinic,
        client: currentClient.value.id,
        folderName,
        parentFolder: parentFolderId,
        scope: 'Programs',
        isShared: false,
        createdBy: userDetails.value.userId,
      };

      const newFolder = await programFoldersDb.create(programFolder);
      folders.value.push(newFolder);
    } finally {
      isLoadingProgramFolder.value = false;
    }
  } // createProgramFolder()

  async function renameProgramFolder(folderId, newFolderName) {
    try {
      isLoadingProgramFolder.value = true;
      const programFoldersDb = new ProgramFoldersDB();
      await programFoldersDb.set({ folderName: newFolderName }, folderId);
      const folder = findFolder(folderId);
      folder.folderName = newFolderName;
    } finally {
      isLoadingProgramFolder.value = false;
    }
  } // renameProgramFolder()

  async function moveFolderToFolder(folderId, parentFolderId) {
    try {
      isLoadingProgramFolder.value = true;

      const programFoldersDb = new ProgramFoldersDB();
      await programFoldersDb.set({ parentFolder: parentFolderId || null }, folderId);

      folders.value = folders.value.filter((folder) => folder.id !== folderId);
      await loadFolders();

      notifySuccess(i18n.t('library.modals.moveFolderToFolderFolder.notifyMoveToFolderSuccess'));
    } finally {
      isLoadingProgramFolder.value = false;
    }
  } // moveFolderToFolder()

  async function moveProgramToFolder(programId, parentFolderId, selectedProgramsIds) {
    const notificationText = programId
      ? i18n.t('library.modals.moveProgramToFolderFolder.notifyMoveToFolderSuccess')
      : i18n.t('library.modals.moveProgramToFolderFolder.notifyMoveSeveralProgramsSuccess');

    isLoadingProgramFolder.value = true;

    try {
      const programsDb = new ProgramsDB();

      const programIdsArray = programId ? [programId] : selectedProgramsIds;

      // split into chunks of 8 (security rules allow max 10 doc access calls per query;
      // 9 fails sometimes - see HR-2163)
      const chunks = [];
      for (let i = 0; i < programIdsArray.length; i += 8) {
        chunks.push(programIdsArray.slice(i, i + 8));
      }

      const programDocsArrays = await Promise.all(
        chunks.map((chunk) => programsDb.readAll([
          [documentId(), 'in', chunk],
        ])),
      );
      const programDocs = programDocsArrays.flat();

      // We should only sync library programs to search engine
      const programsEligibleForSync = programDocs
        .filter((program) => isEmpty(program.client) && !isEmpty(program.clinic));

      const programsToSync = programsEligibleForSync.map((program) => ({
        id: program.id,
        parentFolder: parentFolderId || null,
      }));

      await Promise.all(programIdsArray
        .map((selectedProgId) => programsDb.set({ parentFolder: parentFolderId || null }, selectedProgId)));
      await programsStore.syncLibraryPrograms({ programs: programsToSync, changeType: 'UPDATE' });

      notifySuccess(notificationText);
    } finally {
      isLoadingProgramFolder.value = false;
    }
  } // moveProgramToFolder()

  async function deleteProgramFolder(folderId) {
    try {
      isLoadingProgramFolder.value = true;

      const programFoldersDb = new ProgramFoldersDB();
      await programFoldersDb.delete(folderId);
      folders.value = folders.value.filter((folder) => folder.id !== folderId);
      notifySuccess(i18n.t('library.notifyDeleteProgramFolderSuccess'));

      // if folder contains soft deleted programs, change parent folder to null for these programs
      const programsDb = new ProgramsDB();
      const softDeletedPrograms = await programsDb.readAll([
        ['parentFolder', '==', folderId],
        ['client', '==', currentClient.value.id],
        ['clinic', '==', userDetails.value.clinic],
        ['isDeleted', '==', true],
      ]);
      await Promise.all(softDeletedPrograms
        .map((softDeletedProgram) => programsDb.set({ parentFolder: null }, softDeletedProgram.id)));
    } catch (err) {
      notifyNegative(err.message);
    } finally {
      isLoadingProgramFolder.value = false;
    }
  } // deleteProgramFolder()

  return {
    loadProgramScopedFolders,
    findFolder,
    findTopLevelFolderRecursive,
    renameProgramFolder,
    goToFolder,
    createProgramFolder,
    moveFolderToFolder,
    moveProgramToFolder,
    deleteProgramFolder,
    getFolderPath,
    changeFolder,
    getFolderIcon,
    folders,
    fullFolderPath,
    isLoadingProgramFolder,
  };
}
